// safety/safety-walks/components/WalkFormFields.tsx
// Shared walk-information fields used by both the New Safety Walk page and
// the Edit drawer on the detail page (Overview tab). Controlled via
// `value` + `onChange` — the parent owns persistence.
import React, { useMemo } from 'react';
import { Building2, MapPin, CalendarDays, User } from 'lucide-react';
import { Combobox } from '../../../../components/ui/combobox';
import { MultiSelect } from '../../../../components/ui/multi-select';
import type { ParticipantRef } from '../../../../types/safety-walk.types';

export interface SelectionUser {
    id: number;
    name: string;
    email?: string;
}

export interface ProjectRef {
    id: number;
    projectName: string;
    siteAddress?: string;
}

// Editable walk-information fields. Server-managed fields (walkNumber,
// status, audit, timestamps) are never part of this form.
export interface WalkFormValue {
    project: string;
    location: string;
    walkDate: string;
    conductedBy: string;
    participants: ParticipantRef[];
    generalComments: string;
}

const inputCls = 'w-full h-10 px-3 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

export const WalkFormFields: React.FC<{
    value: WalkFormValue;
    onChange: (patch: Partial<WalkFormValue>) => void;
    projects: ProjectRef[];
    users: SelectionUser[];
}> = ({ value, onChange, projects, users }) => {
    // Conductor value is a user id, or a `name:<name>` snapshot when the
    // stored conductor is no longer in the user list. Options are keyed by
    // user id — names are not unique (two users can share a name), and
    // duplicate Radix Select values break the dropdown.
    const conductorOptions = useMemo(() => {
        const opts = users.map((u) => ({ value: String(u.id), label: u.name }));
        if (value.conductedBy && value.conductedBy.startsWith('name:')) {
            opts.unshift({ value: value.conductedBy, label: value.conductedBy.slice(5) });
        }
        return opts;
    }, [users, value.conductedBy]);

    // Project value is the project NAME (stored snapshot); options are keyed
    // by project id so duplicate project names cannot collide. An unmatched
    // stored name stays selectable so editing never silently drops it.
    const projectOptions = useMemo(() => {
        const names = projects.map((p) => p.projectName);
        const opts = projects.map((p) => ({ id: p.id, value: p.projectName, label: p.projectName }));
        if (value.project && !names.includes(value.project)) {
            opts.unshift({ id: -1, value: value.project, label: value.project });
        }
        return opts;
    }, [projects, value.project]);

    // Participant snapshots whose user is no longer active stay selectable.
    const participantOptions = useMemo(() => {
        const known = new Set(users.map((u) => String(u.id)));
        const opts = users.map((u) => ({ value: String(u.id), label: u.name }));
        for (const p of value.participants) {
            if (p.id !== null && !known.has(String(p.id))) {
                opts.push({ value: String(p.id), label: p.name });
                known.add(String(p.id));
            }
        }
        return opts;
    }, [users, value.participants]);

    return (
        <>
            {/* Walk Information */}
            <div>
                <h3 className="text-sm font-semibold mb-3">Walk Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                            <Building2 size={13} /> PROJECT <span className="text-red-500">*</span>
                        </label>
                        <Combobox
                            options={projectOptions.map((o) => ({ value: o.value, label: o.label }))}
                            value={value.project || undefined}
                            onChange={(v) => onChange({ project: v || '' })}
                            placeholder="Select project..."
                            searchPlaceholder="Search projects..."
                            emptyMessage="No projects found."
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                            <MapPin size={13} /> SITE LOCATION <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={value.location}
                            onChange={(e) => onChange({ location: e.target.value })}
                            placeholder="e.g. Level 3 - Formwork Area"
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                            <CalendarDays size={13} /> WALK DATE <span className="text-red-500">*</span>
                        </label>
                        <input type="date" value={value.walkDate} onChange={(e) => onChange({ walkDate: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                            <User size={13} /> WALK CONDUCTED BY <span className="text-red-500">*</span>
                        </label>
                        <Combobox
                            options={conductorOptions}
                            value={value.conductedBy || undefined}
                            onChange={(v) => onChange({ conductedBy: v || '' })}
                            placeholder="Select conductor..."
                            searchPlaceholder="Search users..."
                            emptyMessage="No users found."
                        />
                    </div>
                </div>
            </div>

            {/* Participants */}
            <div>
                <h3 className="text-sm font-semibold mb-3">Participants</h3>
                <MultiSelect
                    options={participantOptions}
                    selected={value.participants.map((p) => String(p.id))}
                    onChange={(selected) =>
                        onChange({
                            participants: selected
                                .map((id) => {
                                    const known = users.find((u) => String(u.id) === id);
                                    if (known) return { id: known.id, name: known.name };
                                    const snap = value.participants.find((p) => String(p.id) === id);
                                    return snap ? { id: snap.id, name: snap.name } : null;
                                })
                                .filter((p): p is ParticipantRef => Boolean(p)),
                        })
                    }
                    placeholder="Select participants..."
                    emptyMessage="No users available"
                />
            </div>

            {/* General comments */}
            <div>
                <h3 className="text-sm font-semibold mb-3">General Comments</h3>
                <textarea
                    value={value.generalComments}
                    onChange={(e) => onChange({ generalComments: e.target.value })}
                    placeholder="Add general observations or notes about the walk..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
            </div>
        </>
    );
};

export default WalkFormFields;
